import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@bookaburra/ui';
import { SUPPORTED_LOCALES, formatLocaleLabel } from '@bookaburra/i18n';
import { Sparkles, Trash2 } from 'lucide-react';
import {
  autoTranslate,
  deleteTranslation,
  fetchAdminServices,
  upsertTranslation,
  type AdminServiceWithTranslations,
} from '../booking-flow/translations-api';
import { fetchBookingTenant } from '../booking-flow/api';
import { useDocumentTitle } from '../lib/use-document-title';

/** Locales offered for new translations — every supported locale
 *  except the source language (en-AU). Order matches the ABS speaker
 *  ranking from the i18n package. */
const TRANSLATABLE_LOCALES = SUPPORTED_LOCALES.filter((l) => l.code !== 'en-AU');

export const Route = createFileRoute('/admin/$slug/translations')({
  component: TranslationsPage,
});

function TranslationsPage() {
  const { slug } = Route.useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-services', slug],
    queryFn: () => fetchAdminServices(slug),
  });
  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data
      ? `Translations · ${tenantQ.data.tenant.name} Admin`
      : 'Translations',
  );

  return (
    <main className="mx-auto max-w-5xl px-6 py-10 sm:px-12">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Translations</h1>
          <p className="text-sm text-muted-foreground">
            Review and edit before clients see them.
          </p>
        </div>
      </header>

      {isLoading ? <Skeleton /> : null}
      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&rsquo;t load services</AlertTitle>
          <AlertDescription>{(error as Error).message}</AlertDescription>
        </Alert>
      ) : null}

      {data ? (
        <div className="space-y-6">
          {data.services.map((s) => (
            <ServiceRow key={s.id} slug={slug} service={s} />
          ))}
        </div>
      ) : null}
    </main>
  );
}

/** Row state for the open Add/Edit dialog. `null` = closed. */
interface DialogState {
  /** Pre-filled locale (from clicking a Missing tile) or null for blank. */
  locale: string | null;
  /** Existing translation we're editing, if any. */
  existingValue: string | null;
}

function ServiceRow({
  slug,
  service,
}: {
  slug: string;
  service: AdminServiceWithTranslations;
}) {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-services', slug] });

  // Build a per-locale view: every supported locale + any stray
  // locales that exist on the service but aren't in our supported
  // list (e.g. legacy ko-KR seed data). For each: present or missing.
  const supportedCodes = new Set(TRANSLATABLE_LOCALES.map((l) => l.code));
  const existingByLocale = new Map<string, (typeof service.translations)[number]>();
  for (const t of service.translations) {
    // Single field per locale for now (name); take whatever's there.
    if (t.field === 'name') existingByLocale.set(t.locale, t);
  }
  const extraLocales = service.translations
    .filter((t) => t.field === 'name' && !supportedCodes.has(t.locale))
    .map((t) => t.locale);
  const tileLocales = [
    ...TRANSLATABLE_LOCALES.map((l) => l.code),
    ...extraLocales,
  ];

  const removeMut = useMutation({
    mutationFn: (locale: string) =>
      deleteTranslation(slug, {
        entityType: 'service',
        entityId: service.id,
        field: 'name',
        locale,
      }),
    onSuccess: invalidate,
  });

  return (
    <Card
      id={`svc-${service.id}`}
      // scroll-margin pushes the anchor below the sticky app header
      // when the page jumps via #svc-... from the Services page.
      className="scroll-mt-24 p-6"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{service.name}</h2>
          <p className="text-xs text-muted-foreground">
            {service.durationMinutes} min &middot; English is the source language
          </p>
        </div>
      </header>

      <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {tileLocales.map((code) => {
          const existing = existingByLocale.get(code);
          // Stale iff the translation was written when the service
          // name was something other than its current value. Null
          // sourceValue (legacy rows / unknown) → not flagged.
          const stale = Boolean(
            existing &&
              existing.sourceValue != null &&
              existing.sourceValue !== service.name,
          );
          return (
            <LocaleTile
              key={code}
              code={code}
              value={existing?.value ?? null}
              isAuto={existing?.isAuto ?? false}
              stale={stale}
              staleSource={stale ? existing!.sourceValue : null}
              currentSource={service.name}
              onEdit={() =>
                setDialog({
                  locale: code,
                  existingValue: existing?.value ?? null,
                })
              }
              onDelete={existing ? () => removeMut.mutate(code) : undefined}
              deleting={removeMut.isPending && removeMut.variables === code}
            />
          );
        })}
      </div>

      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
      >
        {dialog ? (
          <TranslationDialog
            slug={slug}
            service={service}
            initialLocale={dialog.locale}
            initialValue={dialog.existingValue}
            onClose={() => setDialog(null)}
            onSaved={() => {
              setDialog(null);
              invalidate();
            }}
          />
        ) : null}
      </Dialog>
    </Card>
  );
}

/**
 * One language tile. Two states:
 *  - "present": shows the translated value, optional `auto` badge,
 *    delete button. Click the tile to edit.
 *  - "missing": dashed border, "Missing" tag. Click to add.
 */
function LocaleTile({
  code,
  value,
  isAuto,
  stale,
  staleSource,
  currentSource,
  onEdit,
  onDelete,
  deleting,
}: {
  code: string;
  value: string | null;
  isAuto: boolean;
  /** Translation exists but the source field has changed since. */
  stale: boolean;
  /** Source field value at translation write-time (for the tooltip). */
  staleSource: string | null;
  /** Current source field value (for the tooltip). */
  currentSource: string;
  onEdit: () => void;
  onDelete?: () => void;
  deleting?: boolean;
}) {
  const missing = value === null;
  const label = formatLocaleLabel(code);
  return (
    <div
      className={
        missing
          ? 'group flex flex-col gap-1 rounded-md border border-dashed border-brand-sage/30 bg-brand-cream-shadow/40 px-3 py-2.5 transition-colors hover:border-brand-sage/60 hover:bg-brand-cream-shadow'
          : stale
            ? 'group flex flex-col gap-1 rounded-md border border-amber-500/40 bg-amber-50/40 px-3 py-2.5'
            : 'group flex flex-col gap-1 rounded-md border border-brand-sage/15 bg-brand-cream-shadow/60 px-3 py-2.5'
      }
    >
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="min-w-0 flex-1 text-left text-xs font-medium text-brand-ink-soft/65 transition-colors hover:text-brand-ink-soft"
          aria-label={missing ? `Add ${label} translation` : `Edit ${label} translation`}
        >
          <span className="truncate">{label}</span>
        </button>
        {missing ? (
          <Badge variant="warning" className="shrink-0">
            Missing
          </Badge>
        ) : (
          <div className="flex items-center gap-1.5">
            {stale ? (
              <Badge
                variant="warning"
                className="shrink-0"
                title={`English changed from "${staleSource}" to "${currentSource}" — translation may be outdated.`}
              >
                Stale
              </Badge>
            ) : null}
            {isAuto ? (
              <Badge variant="warning" className="shrink-0">
                auto
              </Badge>
            ) : null}
            {onDelete ? (
              <button
                type="button"
                aria-label="Remove translation"
                onClick={onDelete}
                disabled={deleting}
                className="rounded-md p-1 text-brand-ink-soft/40 transition-colors hover:bg-brand-sage/10 hover:text-brand-ink-soft disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="text-left text-sm text-brand-ink-soft hover:text-brand-ink-soft/80"
      >
        {missing ? (
          <span className="text-brand-ink-soft/45">Click to add a translation</span>
        ) : (
          <span className="line-clamp-2 font-medium">{value}</span>
        )}
      </button>
    </div>
  );
}

function TranslationDialog({
  slug,
  service,
  initialLocale,
  initialValue,
  onClose,
  onSaved,
}: {
  slug: string;
  service: AdminServiceWithTranslations;
  initialLocale: string | null;
  initialValue: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [locale, setLocale] = useState(
    initialLocale ?? TRANSLATABLE_LOCALES[0]!.code,
  );
  const [value, setValue] = useState(initialValue ?? '');
  const [autoUsed, setAutoUsed] = useState(false);

  // Keep state in sync when the dialog is reopened with a different
  // tile selected (Radix re-mounts content on open so this is mostly
  // belt-and-braces).
  useEffect(() => {
    setLocale(initialLocale ?? TRANSLATABLE_LOCALES[0]!.code);
    setValue(initialValue ?? '');
    setAutoUsed(false);
  }, [initialLocale, initialValue]);

  const autoMut = useMutation({
    mutationFn: () =>
      autoTranslate(slug, {
        sourceText: service.name,
        sourceLocale: 'en-AU',
        targetLocale: locale,
        fieldHint: 'name',
      }),
    onSuccess: (result) => {
      setValue(result.translation);
      setAutoUsed(true);
    },
  });

  const saveMut = useMutation({
    mutationFn: () =>
      upsertTranslation(slug, {
        entityType: 'service',
        entityId: service.id,
        field: 'name',
        locale,
        value: value.trim(),
        isAuto: autoUsed,
      }),
    onSuccess: onSaved,
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!value.trim()) return;
    saveMut.mutate();
  }

  const isEditing = initialValue !== null;

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          {isEditing ? 'Edit translation' : 'Add translation'}
        </DialogTitle>
        <DialogDescription>
          Translating <span className="font-medium text-brand-ink-soft">{service.name}</span>
          {' '}from English (en-AU) into another language. Auto-translate uses
          Workers AI; you can edit the result.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-4">
        <FormField label="Language" required>
          {({ id, describedBy }) => (
            <select
              id={id}
              aria-describedby={describedBy}
              value={locale}
              onChange={(e) => {
                setLocale(e.target.value);
                setAutoUsed(false);
              }}
              disabled={isEditing}
              className="h-10 w-full rounded-md border border-input bg-background pl-3 pr-8 text-sm"
            >
              {TRANSLATABLE_LOCALES.map((l) => (
                <option key={l.code} value={l.code}>
                  {formatLocaleLabel(l.code)}
                </option>
              ))}
              {/* Surface stray legacy locale if we're editing one. */}
              {isEditing &&
              !TRANSLATABLE_LOCALES.some((l) => l.code === locale) ? (
                <option value={locale}>{formatLocaleLabel(locale)}</option>
              ) : null}
            </select>
          )}
        </FormField>

        <FormField label="Translated name" required>
          {({ id }) => (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
              <Input
                id={id}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setAutoUsed(false);
                }}
                placeholder="Type translation or press Auto-translate"
              />
              <Button
                type="button"
                variant="outline"
                loading={autoMut.isPending}
                onClick={() => autoMut.mutate()}
                leadingIcon={<Sparkles className="h-4 w-4" />}
              >
                Auto-translate
              </Button>
            </div>
          )}
        </FormField>

        {autoMut.error ? (
          <Alert variant="warning">
            <AlertTitle>Auto-translate failed</AlertTitle>
            <AlertDescription>{(autoMut.error as Error).message}</AlertDescription>
          </Alert>
        ) : null}
        {saveMut.error ? (
          <Alert variant="destructive">
            <AlertTitle>Save failed</AlertTitle>
            <AlertDescription>{(saveMut.error as Error).message}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saveMut.isPending} disabled={!value.trim()}>
            {isEditing ? 'Save changes' : 'Save translation'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-40 animate-pulse rounded-lg bg-secondary" />
      ))}
    </div>
  );
}
